import html2canvas from 'html2canvas'
import {
  Screenshot,
  ScreenshotServiceConfig,
  DEFAULT_SCREENSHOT_CONFIG,
} from '../types/screenshot.types'

let idCounter = 0
const generateId = () => `screenshot-${Date.now()}-${++idCounter}`

/**
 * Compress an image to reduce file size
 */
async function compressImage(
  base64Data: string,
  contentType: string,
  quality: number
): Promise<{ base64Data: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0)

      // Convert to JPEG for better compression (unless it's PNG with transparency)
      const outputType = contentType === 'image/png' ? 'image/png' : 'image/jpeg'
      const compressed = canvas.toDataURL(outputType, quality)
      const parts = compressed.split(',')
      const base64 = parts[1] || ''

      resolve({
        base64Data: base64,
        contentType: outputType,
      })
    }
    img.onerror = () => reject(new Error('Failed to load image for compression'))
    img.src = `data:${contentType};base64,${base64Data}`
  })
}

/**
 * Read a file and convert to base64
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix
      const parts = result.split(',')
      const base64 = parts[1] || ''
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Convert blob to base64
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const parts = result.split(',')
      const base64 = parts[1] || ''
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Get file extension from content type
 */
function getExtension(contentType: string): string {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
  }
  return map[contentType] || 'png'
}

/**
 * Capture screenshot from clipboard paste event
 */
export async function captureFromClipboard(
  config: ScreenshotServiceConfig = DEFAULT_SCREENSHOT_CONFIG
): Promise<Screenshot | null> {
  try {
    const clipboardItems = await navigator.clipboard.read()

    for (const item of clipboardItems) {
      // Find image type in clipboard
      const imageType = item.types.find((type) => type.startsWith('image/'))

      if (imageType) {
        const blob = await item.getType(imageType)
        const base64Data = await blobToBase64(blob)

        // Compress if needed
        const compressed = await compressImage(base64Data, imageType, config.compressionQuality)

        // Validate size
        const sizeBytes = (compressed.base64Data.length * 3) / 4
        if (sizeBytes > config.maxFileSizeBytes) {
          throw new Error(
            `Image exceeds maximum size of ${config.maxFileSizeBytes / 1024 / 1024}MB`
          )
        }

        const screenshot: Screenshot = {
          id: generateId(),
          filename: `clipboard-${Date.now()}.${getExtension(compressed.contentType)}`,
          contentType: compressed.contentType,
          base64Data: compressed.base64Data,
          previewUrl: `data:${compressed.contentType};base64,${compressed.base64Data}`,
          source: 'clipboard',
          createdAt: new Date(),
        }

        return screenshot
      }
    }

    return null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      throw new Error(
        'Clipboard access was denied. Please allow clipboard permissions in your browser settings and try again.'
      )
    }
    console.error('Failed to capture from clipboard:', error)
    throw error
  }
}

/**
 * Capture current screen using html2canvas
 */
export async function captureCurrentScreen(
  config: ScreenshotServiceConfig = DEFAULT_SCREENSHOT_CONFIG
): Promise<Screenshot> {
  try {
    // Capture the entire document body
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: true,
      scale: 1, // Use 1x scale to reduce file size
      logging: false,
      ignoreElements: (element) => {
        // Ignore the support sidebar itself to avoid recursion
        return (
          element.classList.contains('support-sidebar') || element.id === 'support-assistant-root'
        )
      },
    })

    // Convert to JPEG for smaller file size
    const dataUrl = canvas.toDataURL('image/jpeg', config.compressionQuality)
    const parts = dataUrl.split(',')
    const base64Data = parts[1] || ''

    // Validate size
    const sizeBytes = (base64Data.length * 3) / 4
    if (sizeBytes > config.maxFileSizeBytes) {
      // Try more aggressive compression
      const smallerDataUrl = canvas.toDataURL('image/jpeg', 0.6)
      const smallerParts = smallerDataUrl.split(',')
      const smallerBase64 = smallerParts[1] || ''
      const smallerSize = (smallerBase64.length * 3) / 4

      if (smallerSize > config.maxFileSizeBytes) {
        throw new Error(`Screenshot exceeds maximum size even after compression`)
      }

      return {
        id: generateId(),
        filename: `capture-${Date.now()}.jpg`,
        contentType: 'image/jpeg',
        base64Data: smallerBase64,
        previewUrl: smallerDataUrl,
        source: 'capture',
        createdAt: new Date(),
      }
    }

    const screenshot: Screenshot = {
      id: generateId(),
      filename: `capture-${Date.now()}.jpg`,
      contentType: 'image/jpeg',
      base64Data: base64Data,
      previewUrl: dataUrl,
      source: 'capture',
      createdAt: new Date(),
    }

    return screenshot
  } catch (error) {
    console.error('Failed to capture screen:', error)
    throw new Error('Failed to capture screen. Please try uploading a screenshot instead.')
  }
}

/**
 * Handle file upload
 */
export async function handleFileUpload(
  file: File,
  config: ScreenshotServiceConfig = DEFAULT_SCREENSHOT_CONFIG
): Promise<Screenshot> {
  // Validate file type
  if (!config.allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type: ${file.type}. Allowed: ${config.allowedTypes.join(', ')}`)
  }

  // Validate file size
  if (file.size > config.maxFileSizeBytes) {
    throw new Error(`File exceeds maximum size of ${config.maxFileSizeBytes / 1024 / 1024}MB`)
  }

  const base64Data = await fileToBase64(file)

  // Compress if larger than threshold
  const COMPRESSION_THRESHOLD = 1 * 1024 * 1024 // 1MB
  let finalBase64 = base64Data
  let finalContentType = file.type

  if (file.size > COMPRESSION_THRESHOLD) {
    const compressed = await compressImage(base64Data, file.type, config.compressionQuality)
    finalBase64 = compressed.base64Data
    finalContentType = compressed.contentType
  }

  const screenshot: Screenshot = {
    id: generateId(),
    filename: file.name,
    contentType: finalContentType,
    base64Data: finalBase64,
    previewUrl: `data:${finalContentType};base64,${finalBase64}`,
    source: 'upload',
    createdAt: new Date(),
  }

  return screenshot
}

/**
 * Check if clipboard API is available
 */
export function isClipboardSupported(): boolean {
  return 'clipboard' in navigator && 'read' in navigator.clipboard
}

/**
 * Check if html2canvas is likely to work
 */
export function isScreenCaptureSupported(): boolean {
  return typeof html2canvas === 'function'
}
